// Microsoft Teams Monitoring Service
//
// Architecture: client-side only, no hosted backend.
//   - OAuth2 Authorization Code + PKCE via Microsoft Identity Platform
//   - Reads channel messages via Microsoft Graph API
//   - Stores messages + cursors in IndexedDB
//   - Posts summaries/alerts via Graph API (requires bot/app token with chat:send)
//
// Required Microsoft Graph API scopes (delegated):
//   Team.ReadBasic.All          — list joined teams
//   Channel.ReadBasic.All       — list channels in a team
//   ChannelMessage.Read.All     — read channel messages
//   Chat.Read                   — read 1:1 and group chat messages
//   User.Read.All               — resolve user IDs to display names / emails
//   ChannelMessage.Send         — post messages (summaries, alerts)
//
// Users register a Microsoft Entra ID (Azure AD) app and grant admin consent
// or user consent for delegated scopes. VeriGit stores the access + refresh tokens.

import { proxiedFetch } from './cors-proxy';
import { detectSentimentAnomalies } from './messaging-sentiment';
import { getTeamsSettings, saveTeamsSettings } from './messaging-storage';
import type {
  TeamsSettings,
  MessagingChannel,
  MessagingMember,
  MessagingMessage,
  ChannelActivityStats,
  MemberActivityStats,
  MessagingAnomaly,
  MessagingSnapshot,
} from './messaging-types';

// ─── Constants ────────────────────────────────────────────────────────────────

const GRAPH_API = 'https://graph.microsoft.com/v1.0';
const PAGE_LIMIT = 50; // Graph API max per page for messages

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function graphGet<T = unknown>(
  token: string,
  path: string,
  params: Record<string, string> = {},
): Promise<T> {
  const url = new URL(`${GRAPH_API}${path}`);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  const resp = await proxiedFetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });
  if (!resp.ok) throw new Error(`Graph GET ${path} → HTTP ${resp.status}`);
  return resp.json() as Promise<T>;
}

async function graphPost<T = unknown>(
  token: string,
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const resp = await proxiedFetch(`${GRAPH_API}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Graph POST ${path} → HTTP ${resp.status}`);
  return resp.json() as Promise<T>;
}

// ─── Graph API response shapes (minimal) ────────────────────────────────────

interface GraphUser {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName?: string;
}

interface GraphTeam {
  id: string;
  displayName: string;
  description?: string;
}

interface GraphChannel {
  id: string;
  displayName: string;
  membershipType?: 'standard' | 'private' | 'unknownFutureValue';
}

interface GraphMessage {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime?: string;
  from?: {
    user?: { id: string; displayName: string };
    application?: { id: string; displayName: string };
  };
  body: { contentType: 'text' | 'html'; content: string };
  replyToId?: string;
  replies?: { '@odata.count'?: number };
  reactions?: { reactionType: string; createdDateTime: string; user: { user: { id: string } } }[];
  messageType?: string;
}

interface GraphPagedResult<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

// ─── TeamsService ─────────────────────────────────────────────────────────────

export class TeamsService {
  private static instance: TeamsService;

  private constructor() {}

  static getInstance(): TeamsService {
    if (!TeamsService.instance) TeamsService.instance = new TeamsService();
    return TeamsService.instance;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  /** Validate token and return tenant metadata. */
  async testToken(token: string): Promise<{ tenantId: string; tenantName: string }> {
    const org = await graphGet<{ id: string; displayName: string }>(token, '/organization');
    return { tenantId: org.id, tenantName: org.displayName };
  }

  /** Save settings after connecting. */
  async connect(token: string, refreshToken?: string, expiresAt?: string): Promise<TeamsSettings> {
    const meta = await this.testToken(token);
    const settings: TeamsSettings = {
      accessToken: token,
      refreshToken,
      expiresAt,
      tenantId: meta.tenantId,
      tenantName: meta.tenantName,
      monitoredTeamIds: [],
      enabled: true,
    };
    await saveTeamsSettings(settings);
    return settings;
  }

  async disconnect(): Promise<void> {
    await saveTeamsSettings(null);
  }

  async getSettings(): Promise<TeamsSettings | null> {
    return getTeamsSettings();
  }

  // ── Teams & Channels ──────────────────────────────────────────────────────

  /** List all teams the authenticated user has joined. */
  async listJoinedTeams(token: string): Promise<{ id: string; name: string }[]> {
    const data = await graphGet<GraphPagedResult<GraphTeam>>(token, '/me/joinedTeams');
    return data.value.map(t => ({ id: t.id, name: t.displayName }));
  }

  /** List all channels in a team. */
  async listChannels(token: string, teamId: string, teamName: string): Promise<MessagingChannel[]> {
    const data = await graphGet<GraphPagedResult<GraphChannel>>(
      token,
      `/teams/${teamId}/channels`,
    );
    return data.value.map(ch => ({
      id: ch.id,
      name: ch.displayName,
      platform: 'teams' as const,
      teamId,
      teamName,
      isPrivate: ch.membershipType === 'private',
    }));
  }

  /** List all accessible channels across all monitored teams. */
  async listAllChannels(
    token: string,
    teamIds: string[],
    teamNames: Map<string, string>,
  ): Promise<MessagingChannel[]> {
    const all: MessagingChannel[] = [];
    for (const teamId of teamIds) {
      try {
        const chs = await this.listChannels(token, teamId, teamNames.get(teamId) ?? teamId);
        all.push(...chs);
      } catch (err) {
        console.warn(`[TeamsService] listChannels for team ${teamId} failed:`, err);
      }
    }
    return all;
  }

  // ── Members ───────────────────────────────────────────────────────────────

  /** List members of a team. */
  async listTeamMembers(token: string, teamId: string): Promise<MessagingMember[]> {
    const data = await graphGet<GraphPagedResult<GraphUser>>(
      token,
      `/teams/${teamId}/members`,
    );
    return data.value.map(u => ({
      id: u.id,
      platform: 'teams' as const,
      displayName: u.displayName,
      email: u.mail ?? u.userPrincipalName,
      isBot: false,
    }));
  }

  // ── Message Polling ───────────────────────────────────────────────────────

  /**
   * Poll a single channel for messages since `since` (ISO timestamp).
   * Returns parsed messages and the latest ISO timestamp seen.
   */
  async pollChannel(
    token: string,
    teamId: string,
    channelId: string,
    since?: string,
    memberMap: Map<string, string> = new Map(),
  ): Promise<{ messages: MessagingMessage[]; latestTs: string | undefined }> {
    const params: Record<string, string> = {
      $top: String(PAGE_LIMIT),
      $orderby: 'createdDateTime desc',
    };
    if (since) {
      params.$filter = `createdDateTime gt ${since}`;
    }

    const data = await graphGet<GraphPagedResult<GraphMessage>>(
      token,
      `/teams/${teamId}/channels/${channelId}/messages`,
      params,
    );

    const messages: MessagingMessage[] = [];
    let latestTs: string | undefined;

    for (const m of data.value) {
      if (m.messageType && m.messageType !== 'message') continue;
      const authorId = m.from?.user?.id ?? m.from?.application?.id ?? 'unknown';
      const authorName = m.from?.user?.displayName
        ?? memberMap.get(authorId)
        ?? m.from?.application?.displayName
        ?? authorId;

      const ts = m.createdDateTime;
      if (!latestTs || ts > latestTs) latestTs = ts;

      // Strip HTML from body if contentType is html
      const rawText = m.body.content;
      const text = m.body.contentType === 'html'
        ? rawText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : rawText;

      messages.push({
        id: m.id,
        platform: 'teams',
        channelId,
        teamId,
        authorId,
        authorName,
        text,
        timestamp: ts,
        threadId: m.replyToId ?? undefined,
        isReply: !!m.replyToId,
        replyCount: m.replies?.['@odata.count'],
      });
    }

    return { messages, latestTs };
  }

  /**
   * Full poll cycle: iterate all monitored teams + channels, collect new messages.
   */
  async pollAll(
    settings: TeamsSettings,
    channelCursors: Map<string, string>,
    memberMap: Map<string, string>,
  ): Promise<{ allMessages: MessagingMessage[]; updatedCursors: Map<string, string> }> {
    const allMessages: MessagingMessage[] = [];
    const updatedCursors = new Map(channelCursors);

    const teamIds = settings.monitoredTeamIds.length > 0
      ? settings.monitoredTeamIds
      : (await this.listJoinedTeams(settings.accessToken)).map(t => t.id);

    const teamNames = new Map<string, string>();
    const allTeams = await this.listJoinedTeams(settings.accessToken);
    allTeams.forEach(t => teamNames.set(t.id, t.name));

    const channels = await this.listAllChannels(settings.accessToken, teamIds, teamNames);

    for (const ch of channels) {
      if (!ch.teamId) continue;
      try {
        const cursorKey = `${ch.teamId}:${ch.id}`;
        const since = channelCursors.get(cursorKey);
        const { messages, latestTs } = await this.pollChannel(
          settings.accessToken,
          ch.teamId,
          ch.id,
          since,
          memberMap,
        );
        allMessages.push(...messages);
        if (latestTs) updatedCursors.set(cursorKey, latestTs);
      } catch (err) {
        console.warn(`[TeamsService] pollChannel ${ch.id} failed:`, err);
      }
    }

    return { allMessages, updatedCursors };
  }

  // ── Bot Posting ───────────────────────────────────────────────────────────

  /** Post a plain-text message to a Teams channel. */
  async postMessage(
    token: string,
    teamId: string,
    channelId: string,
    text: string,
  ): Promise<void> {
    await graphPost(token, `/teams/${teamId}/channels/${channelId}/messages`, {
      body: { contentType: 'text', content: text },
    });
  }

  /** Post an HTML-formatted message (for rich summaries). */
  async postHtml(
    token: string,
    teamId: string,
    channelId: string,
    html: string,
  ): Promise<void> {
    await graphPost(token, `/teams/${teamId}/channels/${channelId}/messages`, {
      body: { contentType: 'html', content: html },
    });
  }

  // ── Analytics ─────────────────────────────────────────────────────────────

  /** Build per-member and per-channel stats from a flat message list. */
  buildSnapshot(
    messages: MessagingMessage[],
    date: string,
    memberNameMap: Map<string, string> = new Map(),
    channelNameMap: Map<string, string> = new Map(),
  ): Omit<MessagingSnapshot, 'anomalies'> {
    const resolveName = (id: string, fallback: string) =>
      memberNameMap.get(id) || (fallback !== id ? fallback : id);
    const resolveChannel = (id: string) => channelNameMap.get(id) ?? id;

    const memberMap = new Map<string, MemberActivityStats>();
    const channelMap = new Map<string, ChannelActivityStats>();
    const memberChannels = new Map<string, Set<string>>(); // memberId → unique channelIds

    for (const msg of messages) {
      const day = new Date(msg.timestamp).toLocaleDateString('en-CA');

      let ms = memberMap.get(msg.authorId);
      if (!ms) {
        ms = {
          memberId: msg.authorId,
          memberName: resolveName(msg.authorId, msg.authorName),
          platform: 'teams',
          messageCount: 0,
          replyCount: 0,
          totalReactions: 0,
          activeChannelCount: 0,
          dailyCounts: {},
        };
        memberMap.set(msg.authorId, ms);
      }
      ms.messageCount++;
      if (msg.isReply) ms.replyCount++;
      ms.dailyCounts[day] = (ms.dailyCounts[day] ?? 0) + 1;
      if (!memberChannels.has(msg.authorId)) memberChannels.set(msg.authorId, new Set());
      memberChannels.get(msg.authorId)!.add(msg.channelId);
      if (!ms.lastActiveAt || msg.timestamp > ms.lastActiveAt) ms.lastActiveAt = msg.timestamp;

      const chKey = msg.teamId ? `${msg.teamId}:${msg.channelId}` : msg.channelId;
      let cs = channelMap.get(chKey);
      if (!cs) {
        cs = {
          channelId: msg.channelId,
          channelName: resolveChannel(msg.channelId),
          platform: 'teams',
          teamId: msg.teamId,
          messageCount: 0,
          uniqueAuthors: 0,
          totalReactions: 0,
          dailyCounts: {},
          topContributors: [],
        };
        channelMap.set(chKey, cs);
      }
      cs.messageCount++;
      cs.dailyCounts[day] = (cs.dailyCounts[day] ?? 0) + 1;
      if (!cs.lastMessageAt || msg.timestamp > cs.lastMessageAt) cs.lastMessageAt = msg.timestamp;
      if (msg.reactions) cs.totalReactions += msg.reactions.reduce((s, r) => s + r.count, 0);

      // Count reaction participants as active members and credit their given reactions
      if (msg.reactions) {
        for (const reaction of msg.reactions) {
          for (const userId of (reaction.userIds ?? [])) {
            if (!memberMap.has(userId)) {
              memberMap.set(userId, {
                memberId: userId,
                memberName: memberNameMap.get(userId) ?? userId,
                platform: 'teams',
                messageCount: 0,
                replyCount: 0,
                totalReactions: 0,
                activeChannelCount: 0,
                dailyCounts: {},
              });
            }
            memberMap.get(userId)!.totalReactions += 1;
          }
        }
      }
    }
    // Assign unique channel counts from sets accumulated during loop
    for (const [memberId, channels] of memberChannels) {
      const ms = memberMap.get(memberId);
      if (ms) ms.activeChannelCount = channels.size;
    }

    // Compute total reactions
    let totalReactions = 0;
    for (const msg of messages) {
      if (msg.reactions) totalReactions += msg.reactions.reduce((s, r) => s + r.count, 0);
    }

    for (const [chKey, cs] of channelMap) {
      const authors = new Map<string, number>();
      for (const msg of messages) {
        const k = msg.teamId ? `${msg.teamId}:${msg.channelId}` : msg.channelId;
        if (k !== chKey) continue;
        authors.set(msg.authorId, (authors.get(msg.authorId) ?? 0) + 1);
      }
      cs.uniqueAuthors = authors.size;
      cs.topContributors = [...authors.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id, count]) => ({
          memberId: id,
          memberName: memberMap.get(id)?.memberName ?? id,
          count,
        }));
    }

    return {
      platform: 'teams',
      date,
      totalMessages: messages.length,
      totalReactions,
      activeMembers: memberMap.size,
      activeChannels: channelMap.size,
      memberStats: [...memberMap.values()],
      channelStats: [...channelMap.values()],
      quietMembers: [],
    };
  }

  /** Detect anomalies in today's snapshot vs. a historical baseline. */
  detectAnomalies(
    today: Omit<MessagingSnapshot, 'anomalies'>,
    baseline: Omit<MessagingSnapshot, 'anomalies'> | null,
    messages: MessagingMessage[] = [],
  ): MessagingAnomaly[] {
    const anomalies: MessagingAnomaly[] = [];
    const now = new Date().toISOString();

    if (baseline) {
      for (const baseCs of baseline.channelStats) {
        const todayCs = today.channelStats.find(c => c.channelId === baseCs.channelId);
        if (!todayCs && baseCs.messageCount > 5) {
          anomalies.push({
            type: 'no_activity',
            severity: baseCs.messageCount > 20 ? 'high' : 'medium',
            platform: 'teams',
            channelId: baseCs.channelId,
            channelName: baseCs.channelName,
            description: `${baseCs.channelName} had no messages today (baseline: ${baseCs.messageCount} msgs/day).`,
            detectedAt: now,
          });
        }
      }

      for (const cs of today.channelStats) {
        const base = baseline.channelStats.find(b => b.channelId === cs.channelId);
        if (base && cs.messageCount > base.messageCount * 3 && cs.messageCount > 20) {
          anomalies.push({
            type: 'sudden_spike',
            severity: 'medium',
            platform: 'teams',
            channelId: cs.channelId,
            channelName: cs.channelName,
            description: `${cs.channelName} had ${cs.messageCount} messages today (${Math.round(cs.messageCount / base.messageCount)}× normal).`,
            detectedAt: now,
          });
        }
      }

      for (const baseMs of baseline.memberStats) {
        const todayMs = today.memberStats.find(m => m.memberId === baseMs.memberId);
        if (!todayMs && baseMs.messageCount > 3) {
          anomalies.push({
            type: 'sudden_silence',
            severity: 'low',
            platform: 'teams',
            memberId: baseMs.memberId,
            memberName: baseMs.memberName,
            description: `${baseMs.memberName} was active yesterday (${baseMs.messageCount} msgs) but has not posted today.`,
            detectedAt: now,
          });
        }
      }
    }

    for (const cs of today.channelStats) {
      if (cs.uniqueAuthors === 1 && cs.messageCount > 5) {
        anomalies.push({
          type: 'single_author_channel',
          severity: 'low',
          platform: 'teams',
          channelId: cs.channelId,
          channelName: cs.channelName,
          description: `${cs.channelName} only had 1 active participant today (${cs.messageCount} messages).`,
          detectedAt: now,
        });
      }
    }

    // Conflict / sentiment analysis
    anomalies.push(...detectSentimentAnomalies(messages, 'teams', today.channelStats));

    return anomalies;
  }
}

export const teamsService = TeamsService.getInstance();
